/*
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
 * Distributed under the TruVideo proprietary license.
 */
import PropTypes from 'prop-types';
import { hasAnyRoleKey } from '../../utils/scope';

/**
 * What the current user may do with checklists, one flag per action. Reading
 * checklists and comments needs only service_requests.view, so there is no flag for it.
 */
export const buildChecklistPermissions = (can, user) => ({
  create: can.checklists.create(),
  rename: can.checklists.rename(),
  delete: can.checklists.delete(),
  itemAdd: can.checklists.itemAdd(),
  itemEdit: can.checklists.itemEdit(),
  itemStatus: can.checklists.itemStatus(),
  itemReorder: can.checklists.itemReorder(),
  itemDelete: can.checklists.itemDelete(),
  commentAdd: can.checklists.commentAdd(),
  commentEdit: can.checklists.commentEdit(),
  commentDelete: can.checklists.commentDelete(),
  currentUserUid: user?.id ?? null,
  isPlatformAdmin: hasAnyRoleKey(user, ['PLATFORM_ADMIN']),
});

export const NO_CHECKLIST_PERMISSIONS = {};

export const checklistPermissionsShape = PropTypes.shape({
  create: PropTypes.bool,
  rename: PropTypes.bool,
  delete: PropTypes.bool,
  itemAdd: PropTypes.bool,
  itemEdit: PropTypes.bool,
  itemStatus: PropTypes.bool,
  itemReorder: PropTypes.bool,
  itemDelete: PropTypes.bool,
  commentAdd: PropTypes.bool,
  commentEdit: PropTypes.bool,
  commentDelete: PropTypes.bool,
  currentUserUid: PropTypes.string,
  isPlatformAdmin: PropTypes.bool,
});

// Mirrors the server: users edit only their own comments, platform admins any comment.
export const canEditChecklistComment = (permissions, comment) =>
  !!permissions.commentEdit &&
  (permissions.isPlatformAdmin || comment.createdByUserUid === permissions.currentUserUid);

export const apiErrorMessage = (err, fallback) => {
  const msg = err?.response?.data?.message;
  return Array.isArray(msg) ? msg[0] : (msg || fallback);
};

export const initialsOf = (name) =>
  (name || '')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

export const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};
